import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAuth } from "./auth-context";
import { useImportProgress } from "../hooks/useImportProgress";
import { ErrorComponent } from "../components/error/error.component";
import { URLS } from "../services/app-urls";
import { BaseService } from "../services/base-service";
import { AnalysisNotification, NotificationType } from "../types/notification.types";
import { markAllNotificationsAsRead, markMultipleAsRead } from "../utils/notification.utils";
import { useStorage } from "../hooks/useStorage";
import { ACTIVE_CONNECTIONS_KEY, ActiveConnectionEntry } from "../websocket/storage";

interface NotificationContextValue {
  notifications: NotificationType[];
  badgeContent: number | undefined;
  isLoading: boolean;
  markAsRead: (notificationIds: number[]) => Promise<void>;
  markSingleAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: (reason?: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);
const NOTIFICATION_REFRESH_DEBOUNCE_MS = 1500;

const isCanceledRequest = (error: any): boolean => {
  return error?.code === "ERR_CANCELED" || error?.name === "CanceledError";
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authenticated, logout } = useAuth();
  const wsConnections = useStorage<ActiveConnectionEntry[]>(ACTIVE_CONNECTIONS_KEY) ?? [];
  const { isImporting: isImportingBom, historyVersion: historyVersionBom } = useImportProgress('bom');
  const { isImporting: isImportingStockPo, historyVersion: historyVersionStockPo } = useImportProgress('stock_and_po');

  const service = useMemo(() => new BaseService<AnalysisNotification>(URLS.NOTIFICATION), []);

  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const queuedRefreshRef = useRef(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetchedOnceRef = useRef(false);

  const unreadCount = useMemo(() => {
    return notifications.filter(notification => !notification.is_read).length;
  }, [notifications]);

  const badgeContent = useMemo(() => {
    return unreadCount > 0 ? unreadCount : undefined;
  }, [unreadCount]);

  const analysisStatusSignature = useMemo(() => {
    return wsConnections
      .filter(e => e.route === 'analysis')
      .map(e => `${e.id}:${e.status}`)
      .sort()
      .join('|');
  }, [wsConnections]);

  const exportStatusSignature = useMemo(() => {
    return wsConnections
      .filter(
        e =>
          e.route === 'export' ||
          e.route === 'materials-demands-export' ||
          e.route === 'po-components-export' ||
          e.route === 'stock-export',
      )
      .map(e => `${e.storageKey}:${e.status}`)
      .sort()
      .join('|');
  }, [wsConnections]);

  const cancelActiveRequest = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    inFlightRef.current = false;
    queuedRefreshRef.current = false;
    if (mountedRef.current) {
      setIsLoading(false);
    }
  }, []);

  const fetchNotifications = useCallback(async (): Promise<void> => {
    if (!authenticated) {
      cancelActiveRequest();
      setNotifications([]);
      return;
    }

    if (inFlightRef.current) {
      queuedRefreshRef.current = true;
      return;
    }

    inFlightRef.current = true;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (mountedRef.current) {
      setIsLoading(true);
    }

    try {
      service.clearParameters();
      const { data } = await service.getAll({ signal: abortController.signal });

      if (mountedRef.current && !abortController.signal.aborted) {
        setNotifications(data);
      }
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        return;
      }

      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
        return;
      }

      ErrorComponent(error);
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }

      inFlightRef.current = false;

      if (mountedRef.current) {
        setIsLoading(false);
      }

      if (mountedRef.current && authenticated && queuedRefreshRef.current) {
        queuedRefreshRef.current = false;
        void fetchNotifications();
      }
    }
  }, [authenticated, cancelActiveRequest, logout, service]);

  const refreshNotifications = useCallback(
    (_reason?: string): void => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      void fetchNotifications();
    },
    [fetchNotifications],
  );

  const scheduleRefreshNotifications = useCallback(
    (_reason?: string): void => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      refreshTimeoutRef.current = setTimeout(() => {
        refreshTimeoutRef.current = null;
        void fetchNotifications();
      }, NOTIFICATION_REFRESH_DEBOUNCE_MS);
    },
    [fetchNotifications],
  );

  const markAsRead = useCallback(
    async (notificationIds: number[]): Promise<void> => {
      try {
        const payload = { ids: notificationIds };
        await service.getFromRoutePost("mark-as-read", payload);

        setNotifications(prev => markMultipleAsRead(prev, notificationIds));
      } catch (error: any) {
        ErrorComponent(error);
      }
    },
    [service],
  );

  const markSingleAsRead = useCallback(
    async (notificationId: number): Promise<void> => {
      await markAsRead([notificationId]);
    },
    [markAsRead],
  );

  const markAllAsRead = useCallback(async (): Promise<void> => {
    try {
      await service.getFromRoutePost("mark-all-as-read", {});

      setNotifications(prev => markAllNotificationsAsRead(prev));
    } catch (error: any) {
      ErrorComponent(error);
    }
  }, [service]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      cancelActiveRequest();
    };
  }, [cancelActiveRequest]);

  useEffect(() => {
    if (!authenticated) {
      cancelActiveRequest();
      hasFetchedOnceRef.current = false;
      setNotifications([]);
      return;
    }

    if (!hasFetchedOnceRef.current) {
      hasFetchedOnceRef.current = true;
      refreshNotifications("initial-authenticated-fetch");
      return;
    }

    scheduleRefreshNotifications("notification-dependencies");
  }, [
    authenticated,
    refreshNotifications,
    scheduleRefreshNotifications,
    cancelActiveRequest,
    analysisStatusSignature,
    exportStatusSignature,
    isImportingBom,
    historyVersionBom,
    isImportingStockPo,
    historyVersionStockPo,
  ]);

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      badgeContent,
      isLoading,
      markAsRead,
      markSingleAsRead,
      markAllAsRead,
      refreshNotifications,
    }),
    [
      notifications,
      badgeContent,
      isLoading,
      markAsRead,
      markSingleAsRead,
      markAllAsRead,
      refreshNotifications,
    ],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotificationContext = (): NotificationContextValue => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error("useNotificationContext deve ser usado dentro de um NotificationProvider");
  }

  return context;
};
